import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { trackPendoEvent } from 'src/helpers/trackPendoEvent';
import { IProof } from 'src/ts/interfaces';
import { IListCheckEntry } from 'src/ts/interfaces/listCheck';
import {
  humanizeNotRunReason,
  isResolved,
  listCheckChip,
  trendAgreeEdits
} from '../../../helpers/listCheckHelpers';
import { EVIDENCE_FILES, useFraudTrendEvidence } from '../../../hooks/useFraudTrendEvidence';
import { useViewerModeTime } from '../../../hooks/useViewerModeTime';
import {
  ListCheckDetailBody,
  ListCheckDetailHead,
  ListCheckEmptyState,
  ListCheckLede,
  ListCheckLedeRow,
  ListCheckLedeValue,
  ListChecksDetail,
  ListCheckTabButton,
  ListCheckTabStrip,
  ListCheckTitle,
  ListCheckTitleRow
} from '../../../styles';
import FraudTrendFooter from '../FraudTrendFooter';
import ListCheckChip from '../ListCheckChip';
import { listCheckCopy } from '../listCheckCopy';
import { ImageSwitch } from '../textBoxes/styles';
import { PageView } from '../textBoxes/TextBoxesPane';
import { textBoxesCopy } from '../textBoxes/textBoxesCopy';
import OverviewTab from './OverviewTab';
import { TabRow, TemplateLine, ViewSwitchSpacer } from './styles';
import { viewFor } from './views';

interface AdvancedFraudTrendsDetailProps {
  entry: IListCheckEntry;
  currentProof?: IProof;
  surface?: string;
  isSubmitting: boolean;
  canAnswer: boolean;
  onFraudTrendAnswer: (edited: boolean, note?: string) => void;
  onUndo: () => void;
}

const OVERVIEW = 'overview';

const VIEW_EVENT: Record<PageView, string> = { page: 'on_page', list: 'line_by_line' };

const VIEW_ID: Record<PageView, string> = {
  page: 'advanced-fraud-trends-view-on-page',
  list: 'advanced-fraud-trends-view-line-by-line'
};

const NO_VISUAL = { status: 'missing' as const, data: null };

const fraudTrendLede = (entry: IListCheckEntry): ReactNode => {
  if (entry.ruling === 'not_run') {
    return `Gamma Ray could not run this check (${humanizeNotRunReason(
      entry.ruling_reason
    )}), so it has no answer.`;
  }
  if (entry.outcome) return listCheckCopy.fraudTrendLede[entry.outcome];
  if (isResolved(entry)) {
    return (
      <>
        {listCheckCopy.youAnswered}{' '}
        <ListCheckLedeValue>
          {entry.ruling === 'blocked' ? listCheckCopy.edited : listCheckCopy.notEdited}
        </ListCheckLedeValue>
        .
      </>
    );
  }
  return listCheckCopy.fraudTrendLede.inconclusive;
};

const WholeCheckBody: FC<AdvancedFraudTrendsDetailProps> = ({
  entry,
  isSubmitting,
  canAnswer,
  onFraudTrendAnswer,
  onUndo
}) => {
  const prompt =
    trendAgreeEdits(entry.outcome) === null
      ? listCheckCopy.fraudTrendPrompt
      : listCheckCopy.fraudTrendAgreePrompt;
  return (
    <>
      <ListCheckDetailBody data-testid="list-check-detail-body" data-scroll-root>
        {!isResolved(entry) && entry.ruling !== 'not_run' && (
          <ListCheckEmptyState>{prompt}</ListCheckEmptyState>
        )}
      </ListCheckDetailBody>
      <FraudTrendFooter
        entry={entry}
        isSubmitting={isSubmitting}
        canAnswer={canAnswer}
        onAnswer={onFraudTrendAnswer}
        onUndo={onUndo}
      />
    </>
  );
};

const AdvancedFraudTrendsDetail: FC<AdvancedFraudTrendsDetailProps> = (props) => {
  const { entry, currentProof, surface, isSubmitting, canAnswer, onFraudTrendAnswer, onUndo } =
    props;
  const { load, evidenceFor } = useFraudTrendEvidence();
  const [tab, setTab] = useState<string>(OVERVIEW);
  const [pageView, setPageView] = useState<PageView>('page');
  const [showImage, setShowImage] = useState(true);
  const [imageAvailable, setImageAvailable] = useState(false);
  const openedPattern = useRef(false);
  const proofId = currentProof?.id;
  const patterns = entry.patterns ?? [];
  const active = patterns.find((pattern) => pattern.key === tab) ?? null;
  const placement = active?.view === 'text_placement';
  const template = entry.template ?? 'unknown';
  const chip = listCheckChip(entry);
  const evidence = entry.has_evidence
    ? evidenceFor(proofId, entry.check_key, EVIDENCE_FILES.evidences)
    : NO_VISUAL;
  const visual = entry.has_visual
    ? evidenceFor(proofId, entry.check_key, EVIDENCE_FILES.visual)
    : NO_VISUAL;
  const PatternView = active ? viewFor(active.view) : null;
  const clickProps = (pattern: string) => ({ 'data-template': template, 'data-pattern': pattern });

  useViewerModeTime(tab, patterns.length > 0, surface, {
    template,
    pattern: tab,
    ...(placement && { view: VIEW_EVENT[pageView] })
  });

  useEffect(() => {
    if (proofId) load(proofId, entry);
  }, [proofId, entry, load]);

  const onImageAvailable = useCallback((available: boolean) => setImageAvailable(available), []);

  const openTab = (key: string) => {
    if (key === tab) return;
    setImageAvailable(false);
    setTab(key);
    if (key === OVERVIEW) return;
    trackPendoEvent('advanced_fraud_trends_tab_opened', {
      template,
      pattern: key,
      first: !openedPattern.current
    });
    openedPattern.current = true;
  };

  return (
    <ListChecksDetail data-testid="list-check-detail-pane">
      <ListCheckDetailHead>
        <ListCheckTitleRow>
          <ListCheckTitle>{entry.display_name}</ListCheckTitle>
          {chip && <ListCheckChip chip={chip} />}
        </ListCheckTitleRow>
        {entry.template_name && (
          <TemplateLine data-testid="advanced-fraud-trends-template">
            {entry.template_name}
          </TemplateLine>
        )}
        {!patterns.length && (
          <ListCheckLedeRow>
            <ListCheckLede data-testid="advanced-fraud-trends-lede">
              {fraudTrendLede(entry)}
            </ListCheckLede>
          </ListCheckLedeRow>
        )}
      </ListCheckDetailHead>
      {!patterns.length ? (
        <WholeCheckBody {...props} />
      ) : (
        <>
          <TabRow>
            <ListCheckTabStrip role="tablist">
              <ListCheckTabButton
                type="button"
                role="tab"
                id="advanced-fraud-trends-tab-overview"
                data-testid="advanced-fraud-trends-tab-overview"
                {...clickProps(OVERVIEW)}
                aria-selected={tab === OVERVIEW}
                $active={tab === OVERVIEW}
                onClick={() => openTab(OVERVIEW)}
              >
                {listCheckCopy.overviewTab}
              </ListCheckTabButton>
              {patterns.map((pattern) => (
                <ListCheckTabButton
                  key={pattern.key}
                  type="button"
                  role="tab"
                  id={`advanced-fraud-trends-tab-pattern-${pattern.key}`}
                  data-testid="advanced-fraud-trends-tab-pattern"
                  {...clickProps(pattern.key)}
                  aria-selected={tab === pattern.key}
                  $active={tab === pattern.key}
                  onClick={() => openTab(pattern.key)}
                >
                  {pattern.name}
                </ListCheckTabButton>
              ))}
            </ListCheckTabStrip>
            {placement && active && entry.has_visual && pageView === 'page' && (
              <ImageSwitch>
                <input
                  type="checkbox"
                  id="advanced-fraud-trends-page-image"
                  data-testid="advanced-fraud-trends-page-image"
                  {...clickProps(active.key)}
                  checked={imageAvailable && showImage}
                  disabled={!imageAvailable}
                  onChange={(event) => setShowImage(event.target.checked)}
                />
                <i />
                {textBoxesCopy.pageImage}
              </ImageSwitch>
            )}
            <ViewSwitchSpacer />
            {placement && active && (
              <ListCheckTabStrip role="tablist" data-testid="advanced-fraud-trends-view-switch">
                {(['page', 'list'] as PageView[]).map((option) => (
                  <ListCheckTabButton
                    key={option}
                    type="button"
                    role="tab"
                    id={VIEW_ID[option]}
                    data-testid={VIEW_ID[option]}
                    {...clickProps(active.key)}
                    aria-selected={pageView === option}
                    $active={pageView === option}
                    onClick={() => setPageView(option)}
                  >
                    {option === 'page' ? textBoxesCopy.onThePage : textBoxesCopy.lineByLine}
                  </ListCheckTabButton>
                ))}
              </ListCheckTabStrip>
            )}
          </TabRow>
          <ListCheckDetailBody data-testid="list-check-detail-body" data-scroll-root>
            {active && PatternView ? (
              <PatternView
                key={active.key}
                entry={entry}
                pattern={active}
                evidence={evidence}
                visual={visual}
                proofId={proofId}
                pdfUrl={currentProof?.file}
                pageView={pageView}
                showImage={showImage}
                onImageAvailable={onImageAvailable}
              />
            ) : (
              <OverviewTab
                patterns={patterns}
                evidence={evidence}
                outcomeLabels={entry.engine_labels ?? entry.outcome_labels}
              />
            )}
          </ListCheckDetailBody>
          <FraudTrendFooter
            entry={entry}
            isSubmitting={isSubmitting}
            canAnswer={canAnswer}
            onAnswer={onFraudTrendAnswer}
            onUndo={onUndo}
          />
        </>
      )}
    </ListChecksDetail>
  );
};

export default AdvancedFraudTrendsDetail;
